// gloss:file

import { DEFAULT_TIMEZONE } from './constants';
import { formatLocalDay } from './day';
import { formatTimezoneAbbr } from './format';
import { safeParseDate } from './parse';

// gloss
export const getNowInTimezone = (timezone?: string | null): Date => {
  const tz = timezone || DEFAULT_TIMEZONE;
  try {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    const parts = formatter.formatToParts(new Date());
    const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value || '00';
    return new Date(`${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`);
  } catch {
    return new Date();
  }
};

type EventLike = {
  startDate?: string | null;
  endDate?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  timezone?: string | null;
};

// gloss
export type EventWindow = {
  start: Date | null;
  end: Date | null;
};

// why: BrandEvents.startDate/endDate are LongText — most rows are date-only, some carry a full
// why: ISO timestamp. Both reduce to the calendar day before a wall-clock time is appended.
const calendarDay = (date: string): string => {
  const separator = date.indexOf('T');
  return separator === -1 ? date : date.slice(0, separator);
};

// why: advances a YYYY-MM-DD string by exactly one calendar day. The arithmetic runs in UTC so the
// why: increment cannot be skewed by the runtime's local DST rules. It yields a calendar day, not
// why: an instant — re-parsing that day with a time is still subject to the local DST gap.
const nextCalendarDay = (day: string): string => {
  const parsed = new Date(`${day}T00:00:00Z`);
  if (isNaN(parsed.getTime())) return day;
  parsed.setUTCDate(parsed.getUTCDate() + 1);
  return parsed.toISOString().slice(0, 10);
};

const pad2 = (value: number): string => String(value).padStart(2, '0');

const wallClockReading = (date: Date): string => `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;

// why: every production startTime/endTime is HH:MM, but the column is free text — normalise to a
// why: zero-padded reading so two slot boundaries are comparable as strings, and reject anything
// why: that is not a 24h clock reading rather than string-comparing garbage.
const clockReading = (time?: string | null): string | null => {
  const match = /^(\d{1,2}):(\d{2})$/.exec((time ?? '').trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return `${pad2(hours)}:${match[2]}`;
};

const parsableDay = (raw?: string | null): string | null => {
  if (!raw) return null;
  const day = calendarDay(raw);
  return safeParseDate(`${day}T00:00:00`) ? day : null;
};

// gloss
export const getEventWindow = (event: EventLike): EventWindow => {
  // why: a startDate that is absent OR unparseable means no event — deriving an end from endDate
  // why: alone hands isEventPast a closing boundary for a row that has no beginning, and it would
  // why: read as past the moment endDate clears.
  const startDay = parsableDay(event.startDate);
  if (!startDay) return { start: null, end: null };

  const endDay = event.endDate ? calendarDay(event.endDate) : null;
  const startClock = clockReading(event.startTime);
  const endClock = event.endTime ? clockReading(event.endTime) : null;

  // why: clockReading is the only gate — it zero-pads, because a raw `9:00` appends to
  // why: `2026-04-07T9:00`, which is not valid ISO and parses to Invalid Date, and it rejects
  // why: everything else, because `24:00` DOES parse in V8 as the next midnight and would hand
  // why: this row an end that isEventInProgress refuses to honour.
  const start = startClock ? safeParseDate(`${startDay}T${startClock}`) : null;

  // why: endTime is the empty string on 51 production rows and NULL on none, so a start-only
  // why: event closes at its own start rather than never closing.
  const closingTime = endClock ?? startClock;
  if (!closingTime) return { start, end: null };

  // why: a multi-day event runs until endDate — measuring from startDate flips it to past on day
  // why: one — and an endDate that does not parse degrades to startDate rather than never ending.
  const onEndDay = endDay ? safeParseDate(`${endDay}T${closingTime}`) : null;
  const boundaryDay = onEndDay ? (endDay ?? startDay) : startDay;
  const end = onEndDay ?? safeParseDate(`${startDay}T${closingTime}`);
  if (!end) return { start, end: null };

  // why: endTime reading earlier than startTime makes the DAILY slot overnight (22:00 -> 02:00),
  // why: so the last slot opens on the end day and closes the morning after it — whether or not
  // why: endDate is distinct. A 16-day 19:00->10:00 event ends on endDate + 1, not on endDate.
  if (!startClock || !endClock || endClock >= startClock) return { start, end };

  return { start, end: safeParseDate(`${nextCalendarDay(boundaryDay)}T${closingTime}`) ?? end };
};

// gloss
export const isEventPast = (event: EventLike): boolean => {
  const { end } = getEventWindow(event);
  if (!end) return false;
  return getNowInTimezone(event.timezone) > end;
};

// gloss
export const isEventInProgress = (event: EventLike): boolean => {
  const startTime = clockReading(event.startTime);
  const endTime = event.endTime ? clockReading(event.endTime) : null;
  const startDay = parsableDay(event.startDate);
  if (!startTime || !endTime || !startDay) return false;

  const endDay = parsableDay(event.endDate) ?? startDay;
  const now = getNowInTimezone(event.timezone);
  const today = formatLocalDay(now);
  if (!today) return false;
  const nowTime = wallClockReading(now);

  // why: startTime/endTime are the event's DAILY hours and startDate/endDate its day span, so a
  // why: multi-day event is a slot repeated per day, never one contiguous run.
  const isRunningDay = today >= startDay && today <= endDay;
  if (endTime >= startTime) return isRunningDay && nowTime >= startTime && nowTime <= endTime;

  // why: endTime < startTime is an overnight slot — it opens on a running day and closes on the
  // why: morning after, so the final day's tail falls one day past endDay.
  const isOpeningLeg = isRunningDay && nowTime >= startTime;
  const isClosingLeg = today > startDay && today <= nextCalendarDay(endDay) && nowTime <= endTime;
  return isOpeningLeg || isClosingLeg;
};

// why: Intl emits U+202F before AM/PM on newer ICU builds; the rendered contract is a plain
// why: space, so normalize rather than inherit the runtime's ICU version.
const wallClockTime = (date: Date): string =>
  new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    .format(date)
    .replace(/[\u202f\u00a0]/g, ' ');

// gloss
export const formatEventTimeRange = (event: EventLike): string => {
  const { start } = getEventWindow(event);
  if (!start || !event.startDate) return '';

  const tzAbbr = formatTimezoneAbbr(event.timezone, start);
  const startLabel = wallClockTime(start);
  // why: the daily hours are what every surface renders, so the end label comes from endTime on
  // why: the START day — never from the endDate-anchored or overnight-rolled instant.
  const endTime = event.endTime ? clockReading(event.endTime) : null;
  const endLabel = endTime ? safeParseDate(`${calendarDay(event.startDate)}T${endTime}`) : null;
  if (!endLabel) return `${startLabel} ${tzAbbr}`;
  return `${startLabel} - ${wallClockTime(endLabel)} ${tzAbbr}`;
};
